/**
 * Slate File Format (.slate)
 * Open format for layer-based design files
 *
 * A .slate file is a ZIP archive containing:
 * - manifest.json: Version and metadata
 * - design.json: Layers, canvas, variables
 * - preview.png: Thumbnail preview
 * - assets/: Embedded images, fonts, etc.
 */

import type { Template } from './template';
import type { AdFormat } from './formats';
import type { Timeline } from './animation';

// Current format version
export const SLATE_VERSION = '1.0.0';
export const SLATE_EXTENSION = '.slate';
export const SLATE_MIME_TYPE = 'application/x-slate+zip';

/**
 * Manifest file (manifest.json)
 * Contains file metadata and version info
 */
export interface SlateManifest {
  // Format version for compatibility
  version: string;

  // Generator info
  generator: {
    name: string;
    version: string;
  };

  // File metadata
  created: string;
  modified: string;

  // Content info
  name: string;
  description?: string;
  tags?: string[];

  // Preview info
  preview?: {
    width: number;
    height: number;
    format: 'png' | 'jpeg';
  };

  // Assets manifest
  assets?: {
    path: string;
    type: 'image' | 'font' | 'other';
    originalName: string;
  }[];
}

/**
 * Design file (design.json)
 * Contains the actual design data
 */
export interface SlateDesign {
  // Canvas/artboard settings
  canvas: {
    width: number;
    height: number;
    background: Template['canvas']['background'];
  };

  // Format metadata (for ad-specific use)
  format?: {
    id: string;
    name: string;
    platforms?: string;
  };

  // Layer tree
  layers: Template['layers'];

  // Variables for dynamic content
  variables: Template['variables'];

  // Current variable values
  variableValues: Record<string, string>;

  // Animation timeline (optional)
  timeline?: Timeline;
}

/**
 * Complete Slate file structure (in-memory representation)
 */
export interface SlateFile {
  manifest: SlateManifest;
  design: SlateDesign;
  preview?: Blob;
  assets: Map<string, Blob>;
}

/**
 * Create a new manifest
 */
export function createManifest(
  name: string,
  options?: Partial<SlateManifest>
): SlateManifest {
  const now = new Date().toISOString();
  return {
    version: SLATE_VERSION,
    generator: {
      name: 'AdStudio',
      version: '1.0.0',
    },
    created: now,
    modified: now,
    name,
    ...options,
  };
}

/**
 * Create design from template and format
 */
export function createDesignFromTemplate(
  template: Template,
  format: AdFormat,
  variableValues: Record<string, string>
): SlateDesign {
  return {
    canvas: {
      width: format.width,
      height: format.height,
      background: template.canvas.background,
    },
    format: {
      id: format.id,
      name: format.name,
      platforms: format.platforms,
    },
    layers: template.layers,
    variables: template.variables,
    variableValues,
  };
}

/**
 * Convert SlateDesign back to Template format
 */
export function designToTemplate(
  design: SlateDesign,
  manifest: SlateManifest
): Template {
  return {
    version: '2.0' as const,
    metadata: {
      id: `imported-${Date.now()}`,
      name: manifest.name,
      description: manifest.description || '',
      category: 'custom',
      isBuiltin: false,
      createdAt: manifest.created,
      updatedAt: manifest.modified,
      tags: manifest.tags,
    },
    canvas: {
      baseWidth: design.canvas.width,
      baseHeight: design.canvas.height,
      background: design.canvas.background,
    },
    layers: design.layers,
    variables: design.variables,
  };
}

/**
 * Validate a slate manifest
 */
export function isValidManifest(obj: unknown): obj is SlateManifest {
  if (!obj || typeof obj !== 'object') return false;
  const m = obj as Partial<SlateManifest>;
  return (
    typeof m.version === 'string' &&
    typeof m.generator === 'object' &&
    typeof m.created === 'string' &&
    typeof m.name === 'string'
  );
}

/**
 * Validate a slate design
 */
export function isValidDesign(obj: unknown): obj is SlateDesign {
  if (!obj || typeof obj !== 'object') return false;
  const d = obj as Partial<SlateDesign>;
  return (
    typeof d.canvas === 'object' &&
    Array.isArray(d.layers) &&
    Array.isArray(d.variables)
  );
}

/**
 * Check if versions are compatible
 */
export function isCompatibleVersion(version: string): boolean {
  const [major] = version.split('.');
  const [currentMajor] = SLATE_VERSION.split('.');
  return major === currentMajor;
}
